import React, { useState } from "react";
import { toast } from "sonner";
import { Wrench, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Sprint 62.1 — re-solve ONE lineup in place. Pick the players to drop
// (injured ones are pre-checked), choose swap (keep the other 8 locked)
// or rebuild (fresh line, same rules), and the ILP replaces just this row.
export default function FixLineupPanel({ lineup, pool, onFixed, onClose }) {
  const injured = new Set(
    (pool || []).filter((p) => p.is_injured || Number(p.our_proj ?? p.projection ?? 1) <= 0).map((p) => String(p.dk_id)),
  );
  const [drop, setDrop] = useState(() => new Set(
    (lineup.players || []).map((p) => String(p.dk_id)).filter((id) => injured.has(id)),
  ));
  const [mode, setMode] = useState("swap");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setDrop((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.fixLineup(lineup.lineup_id, [...drop], mode);
      toast.success(`Lineup #${lineup.lineup_id} re-solved (${r.mode_used === "swap" ? "surgical swap" : "rebuilt"})`, {
        description: `New proj ${Number(r.lineup?.projection || 0).toFixed(1)} · own Σ ${Number(r.lineup?.total_own || 0).toFixed(1)}%`,
      });
      onFixed?.(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Fix failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-[#FFCC00]/50 bg-[#FFCC00]/5 p-3 mb-3"
      data-testid={`fix-panel-${lineup.lineup_id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold inline-flex items-center gap-1.5">
          <Wrench size={12} weight="fill" /> Fix lineup #{lineup.lineup_id}
        </div>
        <button type="button" onClick={onClose} data-testid={`fix-close-${lineup.lineup_id}`} className="text-zinc-500 hover:text-white">
          <X size={12} weight="bold" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mb-2">
        {(lineup.players || []).map((p) => {
          const id = String(p.dk_id);
          const checked = drop.has(id);
          return (
            <label
              key={id}
              className={`flex items-center gap-2 px-2 py-1 border cursor-pointer text-[11px] mono ${checked ? "border-[#FF3B30]/70 bg-[#FF3B30]/10 text-white" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(id)}
                data-testid={`fix-drop-${lineup.lineup_id}-${id}`}
                className="accent-[#FF3B30]"
              />
              <span className="truncate">{p.name}</span>
              <span className="ml-auto text-zinc-500 shrink-0">{p.position}</span>
              {injured.has(id) && <span className="text-[8px] px-1 border border-red-500/60 text-red-300 shrink-0">INJ</span>}
            </label>
          );
        })}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex border border-zinc-800">
          {[["swap", "Swap · keep the rest"], ["rebuild", "Rebuild · fresh line"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setMode(k)}
              data-testid={`fix-mode-${k}-${lineup.lineup_id}`}
              className={`px-2 py-1 mono text-[10px] uppercase tracking-widest ${mode === k ? "bg-[#FFCC00] text-black font-bold" : "text-zinc-400 hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-zinc-500 mono" data-testid={`fix-summary-${lineup.lineup_id}`}>
          {drop.size} dropped{[...drop].filter((id) => injured.has(id)).length > 0 ? ` (${[...drop].filter((id) => injured.has(id)).length} pre-flagged injured)` : ""} · stays unique vs your other lineups · same contest rules
        </span>
        <button
          type="button"
          onClick={run}
          disabled={busy || (mode === "swap" && drop.size === 0)}
          data-testid={`fix-run-${lineup.lineup_id}`}
          className="ml-auto bg-[#FFCC00] hover:bg-[#e0b400] disabled:opacity-50 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-[10px] px-3 py-1.5 transition-colors"
          title={mode === "swap" && drop.size === 0 ? "Pick at least one player to swap out, or choose Rebuild" : "Re-solve this lineup"}
        >
          {busy ? "Re-solving…" : "Re-solve"}
        </button>
      </div>
    </div>
  );
}
